import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress,
  Alert, TextField, MenuItem, IconButton, Chip, Tooltip,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  InputAdornment, useTheme
} from '@mui/material';
import { Edit, Save, Cancel, Add, Search, LocalOffer } from '@mui/icons-material';
import { getPriceList, updatePriceListItem, createPriceListItem, PriceListItem } from '../../api/priceList';
import { useAuth } from '../../context/AuthContext';

// ─── Tasarım sabitleri ────────────────────────────────────────────────────────
const BRAND  = '#1A3A6B';
const ACCENT = '#c0392b';

const CABLE_TYPES = ['NAYY', 'NA2XY', 'NA2XH', 'AER', 'AAC', 'ACSR'];

const typeColor: Record<string, string> = {
  NAYY: '#1A3A6B', NA2XY: '#0E7490', NA2XH: '#7C3AED',
  AER: '#B45309', AAC: '#16A34A', ACSR: '#DC2626'
};

const actionBtn = (BORDER: string) => ({
  width: 30, height: 30, borderRadius: '6px',
  border: `1px solid ${BORDER}`, color: '#64748b', bgcolor: '#fff',
});

const PriceListPage: React.FC = () => {
  const theme   = useTheme();
  const BORDER  = theme.palette.divider;
  const SURFACE = theme.palette.background.paper;
  const TEXT2   = theme.palette.text.secondary;

  const [items, setItems] = useState<PriceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('');
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editTl, setEditTl] = useState('');
  const [editUsd, setEditUsd] = useState('');
  const [addDialog, setAddDialog] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', unit: 'M', cableType: 'NAYY', priceTl: '', priceUsd: '' });
  const { isAdmin } = useAuth();

  const load = () => {
    setLoading(true);
    getPriceList().then(setItems).catch(() => setError('Yüklenemedi')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items
      .filter(i => {
        const typeOk = !filterType || i.cableType === filterType;
        const searchOk = !search || i.name.toLowerCase().includes(search.toLowerCase());
        return typeOk && searchOk;
      })
      .sort((a, b) => {
        const order = CABLE_TYPES.indexOf(a.cableType) - CABLE_TYPES.indexOf(b.cableType);
        if (order !== 0) return order;
        return a.name.localeCompare(b.name, 'tr');
      }),
    [items, filterType, search]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    items.forEach(i => { counts[i.cableType] = (counts[i.cableType] || 0) + 1; });
    return counts;
  }, [items]);

  const startEdit = (item: PriceListItem) => {
    setEditId(item.id);
    setEditTl(item.priceTl?.toString() || '');
    setEditUsd(item.priceUsd?.toString() || '');
  };

  const saveEdit = async (id: number) => {
    try {
      await updatePriceListItem(id, {
        priceTl: editTl ? parseFloat(editTl) : undefined,
        priceUsd: editUsd ? parseFloat(editUsd) : undefined,
      });
      setEditId(null);
      load();
    } catch { setError('Kayıt hatası'); }
  };

  const handleAdd = async () => {
    try {
      await createPriceListItem({
        name: newItem.name, unit: newItem.unit, cableType: newItem.cableType,
        priceTl: newItem.priceTl ? parseFloat(newItem.priceTl) : undefined,
        priceUsd: newItem.priceUsd ? parseFloat(newItem.priceUsd) : undefined,
        listVersion: '2024-1', active: true,
      });
      setAddDialog(false);
      setNewItem({ name: '', unit: 'M', cableType: 'NAYY', priceTl: '', priceUsd: '' });
      load();
    } catch { setError('Eklenemedi'); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress sx={{ color: BRAND }} /></Box>;

  return (
    <Box sx={{ p: 3, bgcolor: theme.palette.background.default, minHeight: '100vh' }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
              Fiyat Listesi
            </Typography>
            <Chip label="2024-1" size="small"
                  sx={{ height: 22, bgcolor: BRAND, color: 'white', fontWeight: 700,
                    fontSize: '11px', borderRadius: '6px' }} />
          </Box>
          <Typography sx={{ fontSize: '13px', color: TEXT2, mt: 0.5 }}>
            <Box component="span" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>{filtered.length}</Box> ürün
            {filtered.length !== items.length && (
                <Box component="span" sx={{ color: TEXT2 }}> / {items.length} toplam</Box>
            )}
            <Box component="span" sx={{ color: TEXT2 }}> · KDV dahil değildir</Box>
          </Typography>
        </Box>
        {isAdmin && (
            <Button variant="contained" startIcon={<Add />} onClick={() => setAddDialog(true)}
                    sx={{ bgcolor: ACCENT, fontWeight: 600, borderRadius: '8px',
                      px: 2.5, fontSize: '13px', boxShadow: 'none', height: 38,
                      '&:hover': { bgcolor: '#a93226', boxShadow: 'none' } }}>
              Ürün Ekle
            </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Filtreler ── */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: SURFACE }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
              placeholder="Ürün adı ara..." value={search}
              onChange={e => setSearch(e.target.value)} size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: TEXT2 }} /></InputAdornment>,
                sx: { borderRadius: '8px', fontSize: '13px', minWidth: 240 },
              }}
              sx={{ '& fieldset': { borderColor: BORDER } }} />

          <Box sx={{ width: 1, height: 24, bgcolor: BORDER, mx: 0.5 }} />

          <Typography sx={{ fontSize: '12px', color: TEXT2, fontWeight: 600 }}>Tip:</Typography>
          {CABLE_TYPES.map(t => {
            const count = typeCounts[t] || 0;
            if (count === 0) return null;
            const isSelected = filterType === t;
            return (
                <Chip key={t} label={`${t} (${count})`} size="small"
                      onClick={() => setFilterType(isSelected ? '' : t)}
                      sx={{
                        height: 22, fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                        bgcolor: isSelected ? typeColor[t] : 'transparent',
                        color: isSelected ? 'white' : typeColor[t],
                        border: `1.5px solid ${typeColor[t]}`,
                        '&:hover': { bgcolor: typeColor[t], color: 'white', opacity: 0.85 }
                      }} />
            );
          })}

          {(filterType || search) && (
              <Button size="small" onClick={() => { setFilterType(''); setSearch(''); }}
                      sx={{ ml: 'auto', color: TEXT2, textTransform: 'none', fontSize: '12px' }}>
                Temizle
              </Button>
          )}
        </Box>
      </Paper>

      {/* ── Tablo ── */}
      <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden', bgcolor: SURFACE }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  { label: 'Ürün Adı', minWidth: 220 },
                  { label: 'Birim', width: 80 },
                  { label: 'Tip', width: 110 },
                  { label: 'Fiyat (TL/Mt.)', width: 150, align: 'right' },
                  { label: 'Fiyat (USD/Mt.)', width: 150, align: 'right' },
                  { label: 'Versiyon', width: 100, align: 'center' },
                  ...(isAdmin ? [{ label: 'İşlem', width: 100, align: 'center' as const }] : [])
                ].map((h, idx) => (
                    <TableCell key={idx} align={(h.align as any) || 'left'}
                               style={{ background: '#f8f9fc', paddingTop: 12, paddingBottom: 12,
                                 fontSize: 11, fontWeight: 600, color: '#8a92a6',
                                 textTransform: 'uppercase', letterSpacing: '0.05em',
                                 borderBottom: '1px solid #e8eaf0',
                                 minWidth: h.minWidth, width: h.width }}>
                      {h.label}
                    </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 7 : 6} align="center" sx={{ py: 8, color: TEXT2 }}>
                      <Typography sx={{ fontSize: '14px' }}>Filtreyle eşleşen ürün bulunamadı</Typography>
                    </TableCell>
                  </TableRow>
              ) : (
                  filtered.map((item) => (
                      <TableRow key={item.id}
                                sx={{
                                  transition: 'background .1s',
                                  '& td': { borderBottom: '1px solid #e8eaf0', py: 0 },
                                  '&:hover td': { bgcolor: '#fafbfe !important' },
                                }}>
                        <TableCell sx={{ height: 52 }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: BRAND }}>
                            {item.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={item.unit} size="small" variant="outlined"
                                sx={{ fontWeight: 500, fontSize: '11px', height: 20, borderRadius: '6px' }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={item.cableType} size="small"
                                sx={{
                                  height: 22, fontSize: '11px', fontWeight: 700, borderRadius: '6px',
                                  bgcolor: typeColor[item.cableType] || '#64748B', color: 'white',
                                }} />
                        </TableCell>
                        <TableCell align="right">
                          {editId === item.id ? (
                              <TextField size="small" value={editTl}
                                         onChange={e => setEditTl(e.target.value)}
                                         type="number"
                                         sx={{ width: 110, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                          ) : (
                              <Typography sx={{
                                fontSize: '13px', fontWeight: item.priceTl ? 700 : 400,
                                fontFamily: 'monospace',
                                color: item.priceTl ? BRAND : 'text.disabled'
                              }}>
                                {item.priceTl
                                    ? `₺${item.priceTl.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
                                    : '—'}
                              </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {editId === item.id ? (
                              <TextField size="small" value={editUsd}
                                         onChange={e => setEditUsd(e.target.value)}
                                         type="number"
                                         sx={{ width: 110, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                          ) : (
                              <Typography sx={{
                                fontSize: '13px', fontWeight: item.priceUsd ? 700 : 400,
                                fontFamily: 'monospace',
                                color: item.priceUsd ? '#16A34A' : 'text.disabled'
                              }}>
                                {item.priceUsd ? `$${item.priceUsd.toFixed(2)}` : '—'}
                              </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography sx={{ fontSize: '11px', color: TEXT2 }}>{item.listVersion}</Typography>
                        </TableCell>
                        {isAdmin && (
                            <TableCell align="center" sx={{ pr: 2 }}>
                              {editId === item.id ? (
                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                    <Tooltip title="Kaydet">
                                      <IconButton size="small" onClick={() => saveEdit(item.id)}
                                                  style={actionBtn(BORDER)}
                                                  sx={{ '&:hover': { background: '#f0fdf4 !important', color: '#16A34A !important', borderColor: '#86efac !important' } }}>
                                        <Save sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="İptal">
                                      <IconButton size="small" onClick={() => setEditId(null)}
                                                  style={actionBtn(BORDER)}
                                                  sx={{ '&:hover': { background: '#fff0f0 !important', color: '#dc2626 !important', borderColor: '#fca5a5 !important' } }}>
                                        <Cancel sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                              ) : (
                                  <Tooltip title="Fiyat Güncelle">
                                    <IconButton size="small" onClick={() => startEdit(item)}
                                                style={actionBtn(BORDER)}
                                                sx={{ '&:hover': { background: '#fff8ed !important', color: '#d97706 !important', borderColor: '#fed7aa !important' } }}>
                                      <Edit sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                              )}
                            </TableCell>
                        )}
                      </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Ekleme Dialog ── */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth
              PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
        <DialogTitle sx={{ bgcolor: BRAND, color: '#fff', py: 2, px: 3, fontSize: '15px', fontWeight: 600 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalOffer fontSize="small" />
            Yeni Ürün Ekle
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField label="Ürün Adı *" value={newItem.name} size="small"
                       onChange={e => setNewItem({ ...newItem, name: e.target.value })} fullWidth autoFocus />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField select label="Kablo Tipi" value={newItem.cableType} size="small"
                         onChange={e => setNewItem({ ...newItem, cableType: e.target.value })} sx={{ flex: 1 }}>
                {CABLE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField select label="Birim" value={newItem.unit} size="small"
                         onChange={e => setNewItem({ ...newItem, unit: e.target.value })} sx={{ flex: 1 }}>
                <MenuItem value="M">M (Metre)</MenuItem>
                <MenuItem value="KG">KG (Kilogram)</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Fiyat (TL)" type="number" value={newItem.priceTl} size="small"
                         onChange={e => setNewItem({ ...newItem, priceTl: e.target.value })} sx={{ flex: 1 }}
                         InputProps={{ startAdornment: <InputAdornment position="start">₺</InputAdornment> }} />
              <TextField label="Fiyat (USD)" type="number" value={newItem.priceUsd} size="small"
                         onChange={e => setNewItem({ ...newItem, priceUsd: e.target.value })} sx={{ flex: 1 }}
                         InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setAddDialog(false)} sx={{ borderRadius: '8px', color: TEXT2, textTransform: 'none' }}>
            İptal
          </Button>
          <Button variant="contained" onClick={handleAdd} disabled={!newItem.name}
                  sx={{ bgcolor: BRAND, borderRadius: '8px', fontWeight: 600, textTransform: 'none', px: 3, boxShadow: 'none',
                    '&:hover': { bgcolor: '#0f2a52', boxShadow: 'none' } }}>
            Ekle
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PriceListPage;
